"use client"

import { useState, useEffect, Suspense } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Chip,
  Stack,
  Alert,
  AlertTitle,
} from "@mui/material"
import {
  Menu as MenuIcon,
  Restaurant as RestaurantIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Block as BlockIcon,
  Schedule as ScheduleIcon,
  SupervisorAccount as SupervisorAccountIcon, // Added for manager icon
} from "@mui/icons-material"
import { useAuth } from "../context/AuthContext"
import { motion } from "framer-motion"
import MobileBottomNav from "./MobileBottomNav"

const drawerWidth = 280
const mobileDrawerWidth = 280

// Define admin-only routes
const adminOnlyRoutes = [
  "/admin/order-counts",
  "/admin/slot-management",
  "/all-requests",
  "/user-requests",
  "/admin",
  // Add any other admin routes here
]

// Check if current route is admin-only
const isAdminOnlyRoute = (pathname) => {
  return adminOnlyRoutes.some((route) => pathname.startsWith(route))
}

// Enhanced getUserRoleFromToken to also get isManager
export const getUserRoleFromToken = () => {
  try {
    const token = localStorage.getItem("idToken")
    if (!token) return { role: "employee", isManager: false }

    const payload = token.split(".")[1]
    const decodedPayload = JSON.parse(atob(payload))

    // Convert string boolean to actual boolean
    const isManagerValue = decodedPayload.isManager
    const isManager = isManagerValue === true || isManagerValue === "true"

    return {
      role: decodedPayload.user_role || "employee",
      isManager: isManager,
    }
  } catch (error) {
    console.error("Error decoding token:", error)
    return { role: "employee", isManager: false }
  }
}

// Route Protection Component
const RouteProtection = ({ children, userRole, currentPath }) => {
  const navigate = useNavigate()

  // Check if employee is trying to access admin route
  if (userRole === "employee" && isAdminOnlyRoute(currentPath)) {
    return (
      <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            icon={<BlockIcon />}
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 25px rgba(244, 67, 54, 0.2)",
              background: "linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)",
              border: "1px solid rgba(244, 67, 54, 0.2)",
              "& .MuiAlert-icon": {
                color: "#f44336",
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 700, fontSize: "18px" }}>Access Denied</AlertTitle>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You don't have permission to access this page. This area is restricted to administrators only.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <button
                onClick={() => navigate("/my-requests")}
                style={{
                  background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "14px",
                  boxShadow: "0 2px 8px rgba(6, 214, 160, 0.2)",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Go to My Requests
              </button>
            </Box>
          </Alert>
      </Box>
    )
  }

  return children
}

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [selectedComponent, setSelectedComponent] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [userRole, setUserRole] = useState("employee")
  const [isManager, setIsManager] = useState(false) // Added state for manager status
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const { role, isManager: managerStatus } = getUserRoleFromToken()
    setUserRole(role)
    setIsManager(managerStatus)

    // Check if employee is trying to access admin route and redirect
    if (role === "employee" && isAdminOnlyRoute(location.pathname)) {
      console.warn("Employee attempted to access admin route:", location.pathname)
      navigate("/my-requests", { replace: true })
    }
  }, [location.pathname, navigate])

  // Additional protection: Listen for route changes and block admin routes for employees
  useEffect(() => {
    if (userRole === "employee" && isAdminOnlyRoute(location.pathname)) {
      console.warn("Blocking employee access to admin route:", location.pathname)
      navigate("/my-requests", { replace: true })
    }
  }, [location.pathname, userRole, navigate])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleMenuClose()
    logout()
    navigate("/login")
  }

  const getMenuItems = () => {
    if (userRole === "admin") {
      const adminItems = [
        {
          text: "Daily Analytics",
          icon: <AnalyticsIcon />,
          path: "/admin/order-counts",
          color: "#20d5d5",
          bgColor: "rgba(32, 213, 213, 0.15)",
          description: "View daily analytics",
          adminOnly: true,
        },
        {
          text: "All Requests",
          icon: <RestaurantIcon />,
          path: "/all-requests",
          color: "#8b5cf6",
          bgColor: "rgba(139, 92, 246, 0.15)",
          description: "Manage all requests",
          adminOnly: true,
        },
        {
          text: "User Management",
          icon: <HistoryIcon />,
          path: "/user-requests",
          color: "#06d6a0",
          bgColor: "rgba(6, 214, 160, 0.15)",
          description: "User Directory",
          adminOnly: true,
        },
        {
          text: "Slot Management",
          icon: <ScheduleIcon />,
          path: "/admin/slot-management",
          color: "#ff6b35",
          bgColor: "rgba(255, 107, 53, 0.15)",
          description: "Manage food slots",
          adminOnly: true,
        },
        {
          text: "Create Order",
          icon: <AddIcon />,
          path: "/admin/emergency-orders",
          color: "#06d6a0",
          bgColor: "rgba(6, 214, 160, 0.15)",
          description: "Create orders for users",
          adminOnly: true,
        },
        {
        text: "New Request",
        icon: <AddIcon />,
        path: "/requests/new",
        color: "#06d6a0",
        bgColor: "rgba(6, 214, 160, 0.15)",
        description: "Create new request",
        adminOnly: true,
      },
      {
        text: "My Requests",
        icon: <RestaurantIcon />,
        path: "/my-requests",
        color: "#8b5cf6",
        bgColor: "rgba(139, 92, 246, 0.15)",
        description: "View your requests",
        adminOnly: true,
      },
      ]

      // Add manager requests if admin is also a manager
      if (isManager) {
        adminItems.push({
          text: "Team Requests",
          icon: <SupervisorAccountIcon />,
          path: "/manager-requests",
          color: "#ff9800",
          bgColor: "rgba(255, 152, 0, 0.15)",
          description: "Manage team requests",
          adminOnly: false,
        })
      }

      return adminItems
    }

    const employeeItems = [
      {
        text: "New Request",
        icon: <AddIcon />,
        path: "/requests/new",
        color: "#06d6a0",
        bgColor: "rgba(6, 214, 160, 0.15)",
        description: "Create new request",
        adminOnly: false,
      },
      {
        text: "My Requests",
        icon: <RestaurantIcon />,
        path: "/my-requests",
        color: "#8b5cf6",
        bgColor: "rgba(139, 92, 246, 0.15)",
        description: "View your requests",
        adminOnly: false,
      },
    ]

    // Add manager requests if employee is also a manager
    if (isManager) {
      employeeItems.push({
        text: "Team Requests",
        icon: <SupervisorAccountIcon />,
        path: "/manager-requests",
        color: "#ff9800",
        bgColor: "rgba(255, 152, 0, 0.15)",
        description: "Manage team requests",
        adminOnly: false,
      })
    }

    return employeeItems
  }

  const menuItems = getMenuItems()



  // Enhanced navigation handler with role checking
  const handleNavigation = (path, adminOnly = false) => {
    // Double-check: prevent navigation to admin routes for employees
    if (userRole === "employee" && adminOnly) {
      console.warn("Employee attempted to navigate to admin route:", path)
      return
    }

    navigate(path)
    setMobileOpen(false)
  }

  const drawer = (
    <div>
      {/* Logo Section - Custom 7EDGE Design */}
      <div>
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)",
            color: "white",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            borderBottom: "1px solid rgba(32, 213, 213, 0.3)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "radial-gradient(circle at 30% 20%, rgba(32, 213, 213, 0.15) 0%, transparent 60%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {/* Simple 7EDGE Text */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: "32px",
                  color: "white",
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  letterSpacing: "2px",
                  fontFamily: "'Inter', 'Roboto', sans-serif",
                }}
              >
                7EDGE
              </Typography>
            </Box>

            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontSize: "18px",
                mb: 0.5,
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              Meal Program
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.9,
                fontWeight: 500,
                color: "#cbd5e1",
                fontSize: "12px",
              }}
            >
              Smart Food Management
            </Typography>
          </Box>
        </Box>
      </div>

      <Divider sx={{ borderColor: "rgba(32, 213, 213, 0.2)" }} />

      {/* User Profile Section - Improved */}
      <div>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Avatar
            alt={user?.name}
            src={user?.picture}
            sx={{
              width: 64,
              height: 64,
              background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
              fontSize: "24px",
              fontWeight: 700,
              mx: "auto",
              mb: 2,
              boxShadow: "0 4px 12px rgba(32, 213, 213, 0.3)",
              border: "2px solid rgba(32, 213, 213, 0.2)",
            }}
          >
            {user?.name?.charAt(0) || "U"}
          </Avatar>
          <Typography variant="h6" fontWeight={700} sx={{ color: "white", mb: 1, fontSize: "16px" }}>
            {user?.name || "User"}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            <Chip
              label={userRole.toUpperCase()}
              size="small"
              sx={{
                background:
                  userRole === "admin"
                    ? "linear-gradient(135deg, #8b5cf6 0%, #6a4c93 100%)"
                    : "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                color: "white",
                fontWeight: 700,
                fontSize: "10px",
                height: 22,
                borderRadius: 3,
                boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
              }}
            />
            {/* Show Manager badge if user is a manager */}
            {isManager && (
              <Chip
                label="MANAGER"
                size="small"
                sx={{
                  background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "10px",
                  height: 22,
                  borderRadius: 3,
                  boxShadow: "0 4px 15px rgba(255, 152, 0, 0.3)",
                }}
              />
            )}
          </Stack>
        </Box>
      </div>

      <Divider sx={{ borderColor: "rgba(32, 213, 213, 0.2)" }} />

      {/* Navigation Items */}
      <List sx={{ px: 2, py: 2 }}>
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.path
          return (
            <div key={item.text}>
                <ListItem
                  button
                  onClick={() => handleNavigation(item.path, item.adminOnly)}
                  sx={{
                    borderRadius: 3,
                    mb: 1.5,
                    background: isActive
                      ? "linear-gradient(135deg, rgba(32, 213, 213, 0.2) 0%, rgba(32, 213, 213, 0.1) 100%)"
                      : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
                    backdropFilter: "blur(10px)",
                    border: isActive ? "1px solid rgba(32, 213, 213, 0.3)" : "1px solid rgba(255,255,255,0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: "linear-gradient(135deg, rgba(32, 213, 213, 0.15) 0%, rgba(32, 213, 213, 0.08) 100%)",
                      border: "1px solid rgba(32, 213, 213, 0.4)",
                      boxShadow: "0 8px 25px rgba(32, 213, 213, 0.2)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "#20d5d5" : item.color,
                      minWidth: 45,
                      "& svg": {
                        fontSize: 22,
                        filter: isActive ? "drop-shadow(0 2px 4px rgba(32, 213, 213, 0.3))" : "none",
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        fontWeight={isActive ? 700 : 600}
                        sx={{
                          color: "white",
                          fontSize: "14px",
                        }}
                      >
                        {item.text}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: isActive ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.7)",
                          fontWeight: 500,
                          fontSize: "11px",
                        }}
                      >
                        {item.description}
                      </Typography>
                    }
                  />
                  {isActive && (
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(32, 213, 213, 0.5)",
                      }}
                    />
                  )}
                </ListItem>
            </div>
          )
        })}
      </List>
    </div>
  )

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Enhanced App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(32, 213, 213, 0.2)",
          color: "#f8fafc",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Toolbar sx={{ py: { xs: 0.5, sm: 1 }, minHeight: { xs: "56px !important", sm: "64px !important" }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: "none" },
              color: "#20d5d5",
              backgroundColor: "rgba(32, 213, 213, 0.15)",
              "&:hover": {
                backgroundColor: "rgba(32, 213, 213, 0.25)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Left Side - Title and Breadcrumb */}
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #20d5d5 0%, #8b5cf6 50%, #06d6a0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.1rem", md: "1.4rem" },
                  }}
                >
                  Meal Program
                </Typography>

              <Chip
                icon={<DashboardIcon sx={{ fontSize: 14 }} />}
                label={
                  userRole === "admin"
                    ? isManager
                      ? "Admin Dashboard"
                      : "Admin Dashboard"
                    : isManager
                      ? "My Portal"
                      : "My Portal"
                }
                size="small"
                sx={{
                  background:
                    userRole === "admin"
                      ? "linear-gradient(135deg, #8b5cf6 0%, #6a4c93 100%)"
                      : isManager
                        ? "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                        : "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 26,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            </Stack>

            <Typography
              variant="caption"
              sx={{
                color: "rgba(248, 250, 252, 0.7)",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
                fontSize: "11px",
              }}
            >
              Welcome back, {user?.name || "User"} •{" "}
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          </Box>

          {/* Right Side - User Menu */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Account settings" arrow>
                <Box
                  onClick={handleMenuOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: "6px 10px",
                    borderRadius: 3,
                    background: "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.8) 100%)",
                    border: "1px solid rgba(32, 213, 213, 0.2)",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: "linear-gradient(135deg, rgba(51, 65, 85, 0.9) 0%, rgba(30, 41, 59, 0.9) 100%)",
                      border: "1px solid rgba(32, 213, 213, 0.3)",
                      boxShadow: "0 8px 25px rgba(32, 213, 213, 0.2)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Avatar
                    alt={user?.name}
                    src={user?.picture}
                    sx={{
                      width: 32,
                      height: 32,
                      background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                      fontWeight: 700,
                      fontSize: "12px",
                      border: "2px solid rgba(255,255,255,0.2)",
                      boxShadow: "0 2px 8px rgba(32, 213, 213, 0.3)",
                    }}
                  >
                    {user?.name?.charAt(0) || "U"}
                  </Avatar>

                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: "#f8fafc",
                        lineHeight: 1.2,
                        fontSize: "13px",
                      }}
                    >
                      {user?.name || "User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(248, 250, 252, 0.7)",
                        textTransform: "capitalize",
                        fontWeight: 500,
                        fontSize: "10px",
                      }}
                    >
                      {userRole}
                      {/* {isManager ? " + Manager" : ""} */}
                    </Typography>
                  </Box>
                </Box>
            </Tooltip>
          </Stack>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                mt: 1,
                minWidth: 200,
                background: "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(32, 213, 213, 0.2)",
                color: "#f8fafc",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose()
                navigate("/profile")
              }}
              sx={{
                py: 1.5,
                borderRadius: 2,
                mx: 1,
                mt: 1,
                color: "#f8fafc",
                "&:hover": {
                  background: "linear-gradient(135deg, rgba(32, 213, 213, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)",
                },
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: "#20d5d5" }} />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>
                Profile Settings
              </Typography>
            </MenuItem>
            <Divider sx={{ my: 1, borderColor: "rgba(32, 213, 213, 0.2)" }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                borderRadius: 2,
                mx: 1,
                mb: 1,
                color: "#f87171",
                "&:hover": {
                  backgroundColor: "rgba(248, 113, 113, 0.15)",
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: "#f87171" }} />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>
                Sign Out
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Dark Sidebar */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: mobileDrawerWidth,
              background: "linear-gradient(180deg, #1e293b 0%, #334155 50%, #475569 100%)",
              color: "white",
              border: "none",
              // Mobile-specific improvements
              maxWidth: "85vw",
              height: "100vh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            },
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              background: "linear-gradient(180deg, #1e293b 0%, #334155 50%, #475569 100%)",
              color: "white",
              border: "none",
              boxShadow: "4px 0 20px rgba(30, 41, 59, 0.5)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content with Route Protection */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
          // Mobile-specific improvements
          paddingBottom: { xs: "80px", sm: "24px" }, // Extra bottom padding for mobile
          overflowX: "hidden",
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important" }} />
        <RouteProtection userRole={userRole} currentPath={location.pathname}>
          <Suspense>
            <Outlet context={{ setSelectedComponent: setSelectedComponent }} />
          </Suspense>
        </RouteProtection>
      </Box>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </Box>
  )
}

export default Layout
